import { Button, Input, Table, Tag, Tooltip } from "antd";
import moment from "moment";
import { ReactSortable } from "react-sortablejs";
import { Image as AntImage } from "antd";
import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router";
import Plus from "../../assets/images/plus.svg";
import ConfirmationBox from "../../components/ConfirmationBox";
import DeleteModal from "../../components/DeleteModal";
import SectionWrapper from "../../components/SectionWrapper";
import apiPath from "../../constants/apiPath";
import { AppStateContext } from "../../context/AppContext";
import lang from "../../helper/langHelper";
import { Severty, ShowToast } from "../../helper/toast";
import useDebounce from "../../hooks/useDebounce";
import useRequest from "../../hooks/useRequest";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";

import AddForm from "./AddForm";

function Index() {
  const heading = lang("Banner") + " " + lang("Management");
  const { setPageHeading } = useContext(AppStateContext);

  const sectionName = "Add Banner";
  const { request } = useRequest();
  const navigate = useNavigate();

  const api = {
    list: apiPath.bannerList,
    updateOrder: apiPath.updateOrderBanner,
    addEdit: apiPath.addEditBanner,
    delete: apiPath.bannerDelete,
    statusChange: apiPath.bannerStatusChange,
  };
  const [searchText, setSearchText] = useState("");
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refresh, setRefresh] = useState(false);
  const [visible, setVisible] = useState(false);
  const [selected, setSelected] = useState(null);
  const [showDelete, setShowDelete] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
  const debouncedSearchText = useDebounce(searchText, 300);
  const { showConfirm } = ConfirmationBox();

  const columns = [
    {
      title: "Banner",
      dataIndex: "image",
      key: "image",
      render: (_, { image }) => (
        <AntImage
          src={image}
          alt="banner"
          width={80}
          height={40}
          style={{
            objectFit: "cover",
            borderRadius: 6,
            border: "1px solid #eee",
          }}
          preview={{ mask: "Preview" }}
        />
      ),
    },
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
      render: (text) => text || "-",
    },
    {
      title: "Redirect URL",
      dataIndex: "redirect_url",
      key: "redirect_url",
      render: (text) =>
        text ? (
          <a href={text} target="_blank" rel="noopener noreferrer">
            Visit Link
          </a>
        ) : (
          "-"
        ),
    },
    {
      title: "Visibility",
      dataIndex: "visibility",
      key: "visibility",
      render: (text) => text?.toUpperCase() || "-",
    },
    {
      title: "Start Date",
      dataIndex: "start_date",
      key: "start_date",
      render: (date) => (date ? moment(date).format("DD-MM-YYYY") : "-"),
    },
    {
      title: "End Date",
      dataIndex: "end_date",
      key: "end_date",
      render: (date) => (date ? moment(date).format("DD-MM-YYYY") : "-"),
    },
    {
      title: "Status",
      dataIndex: "is_active",
      key: "is_active",
      filters: [
        { text: "Active", value: true },
        { text: "Inactive", value: false },
      ],
      render: (_, { is_active, _id }) => (
        <Tag
          color={is_active ? "green" : "red"}
          onClick={() =>
            showConfirm({
              record: _id,
              path: api.statusChange,
              onLoading: () => setLoading(true),
              onSuccess: () => {
                setRefresh((prev) => !prev);
                ShowToast(
                  `Status changed to ${
                    !is_active ? "Active" : "Inactive"
                  } successfully`,
                  Severty.SUCCESS
                );
              },
            })
          }
          style={{ cursor: "pointer" }}
        >
          {is_active ? "Active" : "Inactive"}
        </Tag>
      ),
    },
    {
      title: "Action",
      key: "action",
      fixed: "right",
      render: (_, record) => (
        <div className="d-flex gap-2">
          <Tooltip title="Edit" color="purple">
            <Button
              className="btnStyle primary_btn"
              onClick={() => {
                setSelected(record);
                setVisible(true);
              }}
            >
              <i className="fas fa-edit"></i>
            </Button>
          </Tooltip>
          <Tooltip title="Delete" color="purple">
            <Button
              className="btnStyle deleteDangerbtn"
              onClick={() => {
                setSelected(record);
                setShowDelete(true);
              }}
            >
              <i className="fas fa-trash"></i>
            </Button>
          </Tooltip>
        </div>
      ),
    },
  ];

  const fetchData = (pagination, filters) => {
    const filterActive = filters ? filters.is_active : null;

    request({
      url:
        api.list +
        `?status=${filterActive ? filterActive.join(",") : ""}&page=${
          pagination.current
        }&pageSize=${pagination.pageSize}&search=${debouncedSearchText}`,
      method: "GET",
      onSuccess: ({ data }) => {
        setList(data?.list?.docs || []);
        setPagination({
          current: data?.list?.page,
          total: data?.list?.totalDocs,
          pageSize: data?.list?.limit,
        });
        setLoading(false);
      },
      onError: (error) => {
        setLoading(false);
        ShowToast(error, Severty.ERROR);
      },
    });
  };
  const onSaveOrder = () => {
    const orderedList = list.map((item, index) => ({
      _id: item._id,
      order: index + 1,
    }));

    request({
      url: api.updateOrder,
      method: "PUT",
      data: { orderList: orderedList },
      onSuccess: () => {
        ShowToast("Banner order saved", Severty.SUCCESS);
        setRefresh((prev) => !prev);
      },
      onError: (err) => {
        ShowToast(err, Severty.ERROR);
      },
    });
  };

  useEffect(() => {
    setPageHeading(heading);
  }, []);

  useEffect(() => {
    setLoading(true);
    fetchData({ ...pagination, current: 1 });
  }, [refresh, debouncedSearchText]);

  const handleChange = (pagination, filters) => {
    fetchData(pagination, filters);
  };

  return (
    <>
      <SectionWrapper
        cardHeading={lang("Banner") + " " + lang("List")}
        extra={
          <div className="d-flex align-items-center gap-3 text-head_right_cont">
            <div className="pageHeadingSearch d-flex gap-2">
              <Input.Search
                className="searchInput"
                placeholder="Search by Title or URL"
                allowClear
                onChange={(e) => setSearchText(e.target.value.trimStart())}
              />
              <Button
                className="primary_btn btnStyle"
                onClick={() => {
                  setVisible(true);
                  setSearchText("");
                }}
              >
                <span className="add-Ic">
                  <img src={Plus} alt="add" />
                </span>
                {sectionName}
              </Button>
              <Button className="primary_btn btnStyle" onClick={onSaveOrder}>
                Save Order
              </Button>
            </div>
          </div>
        }
      >
        <div className="d-flex flex-column gap-2">
          <DragDropContext
            onDragEnd={(result) => {
              if (!result.destination) return;

              const newList = Array.from(list);
              const [movedItem] = newList.splice(result.source.index, 1);
              newList.splice(result.destination.index, 0, movedItem);
              setList(newList);
            }}
          >
            <Droppable droppableId="banner-list">
              {(provided) => (
                <div
                  {...provided.droppableProps}
                  ref={provided.innerRef}
                  className="d-flex flex-column gap-2"
                >
                  {list.map((item, index) => (
                    <Draggable
                      key={item._id}
                      draggableId={item._id}
                      index={index}
                    >
                      {(provided) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                          className="d-flex justify-content-between align-items-center border rounded p-2 mb-2"
                          style={{
                            background: "#fff",
                            cursor: "grab",
                            ...provided.draggableProps.style,
                          }}
                        >
                          <div className="d-flex align-items-center gap-3">
                            <AntImage
                              src={item.image}
                              alt="banner"
                              width={80}
                              height={40}
                              style={{
                                objectFit: "cover",
                                borderRadius: 6,
                                border: "1px solid #eee",
                              }}
                              preview={{ mask: "Preview" }}
                            />
                            <div>
                              <div className="d-flex align-items-center gap-2">
                                <strong>{item.title || "-"}</strong>
                                <Tag
                                  className="new-active-class-main"
                                  color={item.is_active ? "green" : "red"}
                                  onClick={() =>
                                    showConfirm({
                                      record: item._id,
                                      path: api.statusChange,
                                      onLoading: () => setLoading(true),
                                      onSuccess: () => {
                                        setRefresh((prev) => !prev);
                                        ShowToast(
                                          `Status changed to ${
                                            !item.is_active
                                              ? "Active"
                                              : "Inactive"
                                          } successfully`,
                                          Severty.SUCCESS
                                        );
                                      },
                                    })
                                  }
                                  style={{ cursor: "pointer" }}
                                >
                                  {item.is_active ? "Active" : "Inactive"}
                                </Tag>
                              </div>
                              <span style={{ fontSize: 12, color: "#555" }}>
                                {item.redirect_url ? (
                                  <a
                                    href={item.redirect_url}
                                    target="_blank"
                                    rel="noreferrer"
                                  >
                                    {item.redirect_url.length > 40
                                      ? item.redirect_url.slice(0, 40) + "..."
                                      : item.redirect_url}
                                  </a>
                                ) : (
                                  "-"
                                )}
                              </span>
                            </div>
                          </div>

                          <div className="d-flex gap-2">
                            <Tooltip title="Edit" color="purple">
                              <Button
                                className="btnStyle primary_btn"
                                onClick={() => {
                                  setSelected(item);
                                  setVisible(true);
                                }}
                              >
                                <i className="fas fa-edit" />
                              </Button>
                            </Tooltip>
                            <Tooltip title="Delete">
                              <Button
                                className="btnStyle deleteDangerbtn"
                                onClick={() => {
                                  setSelected(item);
                                  setShowDelete(true);
                                }}
                              >
                                <i className="fas fa-trash" />
                              </Button>
                            </Tooltip>
                          </div>
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        </div>
      </SectionWrapper>

      {visible && (
        <AddForm
          section={sectionName}
          api={api}
          show={visible}
          hide={() => {
            setVisible(false);
            setSelected(null);
          }}
          data={selected}
          refresh={() => setRefresh((prev) => !prev)}
        />
      )}

      {showDelete && (
        <DeleteModal
          title={"Delete Banner"}
          subtitle={`Are you sure you want to delete this banner?`}
          show={showDelete}
          hide={() => {
            setShowDelete(false);
            setSelected(null);
          }}
          onOk={() => {
            request({
              url: `${api.delete}/${selected?._id}`,
              method: "POST",
              onSuccess: () => {
                ShowToast("Banner deleted successfully", Severty.SUCCESS);
                setRefresh((prev) => !prev);
                setShowDelete(false);
              },
              onError: (err) => ShowToast(err, Severty.ERROR),
            });
          }}
        />
      )}
    </>
  );
}

export default Index;
